import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Flame, Play, Heart, Users, ArrowLeft, Crown, Share2, Download, Loader2, Film, Quote } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };

const SHARE_THEMES = [
  { id: "sunset", label: "Sunset", bg: "linear-gradient(135deg,#1e1b4b 0%,#4c1d95 45%,#b45309 100%)" },
  { id: "ocean", label: "Ocean", bg: "linear-gradient(135deg,#082f49 0%,#0e7490 50%,#0891b2 100%)" },
  { id: "neon", label: "Neon", bg: "linear-gradient(135deg,#111827 0%,#6d28d9 50%,#db2777 100%)" },
  { id: "forest", label: "Forest", bg: "linear-gradient(135deg,#052e16 0%,#166534 55%,#65a30d 100%)" },
  { id: "midnight", label: "Midnight", bg: "linear-gradient(135deg,#020617 0%,#1e293b 60%,#334155 100%)" },
];

export default function CreatorRace() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fame, setFame] = useState(null);
  const [allTime, setAllTime] = useState([]);
  const [champ, setChamp] = useState(null);
  const [mine, setMine] = useState(null);
  const [recap, setRecap] = useState(null);
  const [topFans, setTopFans] = useState([]);
  const [reigning, setReigning] = useState(null);
  const [cotw, setCotw] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [theme, setTheme] = useState("sunset");
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/arcade/weekly-momentum", { params: { limit: 50 } })
      .then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/arcade/hall-of-fame", { params: { limit: 24 } })
      .then((r) => { setFame(r.data.winners || []); setAllTime(r.data.all_time || []); setChamp(r.data.month_champion || null); }).catch(() => setFame([]));
    api.get("/arcade/my-race").then((r) => setMine(r.data)).catch(() => setMine(null));
    api.get("/arcade/weekly-recap").then((r) => setRecap(r.data)).catch(() => setRecap(null));
    api.get("/arcade/top-fans", { params: { limit: 20 } }).then((r) => { setTopFans(r.data.fans || []); setReigning(r.data.reigning_champion || null); }).catch(() => setTopFans([]));
    api.get("/arcade/cheer-of-the-week").then((r) => setCotw(r.data.cheer || null)).catch(() => setCotw(null));
  }, []);

  // Celebrate a milestone crown streak (4/8/12 weeks) once per streak value per browser.
  useEffect(() => {
    if (reigning && [4, 8, 12].includes(reigning.streak)) {
      const key = `gv_streak_celebrated_${reigning.id}_${reigning.streak}`;
      if (!localStorage.getItem(key)) {
        const t = setTimeout(() => {
          confetti({ particleCount: 160, spread: 85, origin: { y: 0.3 }, zIndex: 9999 });
          toast.success(`👑 ${reigning.name} hit a ${reigning.streak}-week crown streak — ${reigning.tier?.name} tier!`, { duration: 6000 });
        }, 500);
        try { localStorage.setItem(key, "1"); } catch {}
        return () => clearTimeout(t);
      }
    }
  }, [reigning]);

  const themeBg = (SHARE_THEMES.find((t) => t.id === theme) || SHARE_THEMES[0]).bg;

  const shareCard = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "gameview-weekly-rank.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My GameView weekly rank", text: "Climbing the GameView Creator of the Week race! 🏆" });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl; a.download = "gameview-weekly-rank.png"; a.click();
        toast.success("Rank card downloaded — share it anywhere! 🎉");
      }
    } catch (e) { toast.error("Couldn't create the image. Try again."); }
    finally { setSharing(false); }
  };

  if (data === false) return <div className="p-8"><EmptyState emoji="🏁" title="Race unavailable" text="Couldn't load the weekly race right now." /></div>;
  if (!data) return <Spinner />;
  const rows = data.creators || [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="creator-race-page">
      <Button variant="ghost" size="sm" className="rounded-full mb-3 -ml-2" onClick={() => navigate("/app/discover")} data-testid="race-back">
        <ArrowLeft className="w-4 h-4" /> Discover
      </Button>
      <div className="rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.10] via-orange-500/[0.06] to-transparent p-6 mb-6">
        <div className="flex items-center gap-2 text-xs font-bold text-amber-500 uppercase tracking-wide mb-1">
          <Trophy className="w-4 h-4 fill-amber-400" /> Creator of the Week race
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">The weekly momentum race</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-xl">
          Ranked by 7-day momentum — new plays, new followers and tips. Publish and share your games to climb.
          Resets every week; the leader becomes <span className="text-amber-500 font-semibold">Creator of the Week</span>.
        </p>
        <Button onClick={() => setShareOpen(true)} className="rounded-full mt-4 bg-gradient-to-r from-amber-400 to-orange-500 text-black hover:opacity-90" data-testid="race-share-btn">
          <Share2 className="w-4 h-4" /> Share my rank
        </Button>
      </div>

      {recap && (recap.creator_of_week || (recap.new_games || []).length > 0) && (
        <div className="mb-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="recap-preview">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">
            <Film className="w-4 h-4 text-fuchsia-400" /> This week's recap · preview
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            {recap.creator_of_week && (
              <button onClick={() => navigate(`/app/creators/${recap.creator_of_week.owner_id}`)} className="rounded-xl bg-amber-400/[0.08] border border-amber-400/25 p-3 text-left card-hover" data-testid="recap-cow">
                <div className="text-[10px] font-bold uppercase text-amber-500 mb-1">🏆 Creator of the Week</div>
                <div className="flex items-center gap-2"><span className="text-2xl">{recap.creator_of_week.avatar_emoji}</span><span className="font-bold truncate">{recap.creator_of_week.owner_name}</span></div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{recap.creator_of_week.plays_7d} plays this week</div>
              </button>
            )}
            <div className="rounded-xl bg-orange-400/[0.06] border border-orange-400/20 p-3" data-testid="recap-risers">
              <div className="text-[10px] font-bold uppercase text-orange-400 mb-1">🔥 Rising this week</div>
              {(recap.risers || []).length === 0 ? <div className="text-xs text-[hsl(var(--muted-foreground))]">No risers yet</div> :
                <ul className="text-xs space-y-0.5">{recap.risers.slice(0, 4).map((r) => <li key={r.owner_id} className="truncate">{r.avatar_emoji} {r.owner_name}</li>)}</ul>}
            </div>
            <div className="rounded-xl bg-fuchsia-400/[0.06] border border-fuchsia-400/20 p-3" data-testid="recap-new-games">
              <div className="text-[10px] font-bold uppercase text-fuchsia-400 mb-1">🎮 Fresh games</div>
              {(recap.new_games || []).length === 0 ? <div className="text-xs text-[hsl(var(--muted-foreground))]">No new games</div> :
                <ul className="text-xs space-y-0.5">{recap.new_games.slice(0, 4).map((g) => <li key={g.id} className="truncate">{g.cover_emoji} {g.title}</li>)}</ul>}
            </div>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3">📬 This goes out to everyone who's opted into the weekly digest, every Sunday.</p>
        </div>
      )}

      {champ && (
        <div className="mb-6 rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-400/[0.10] to-transparent p-4 flex items-center gap-4" data-testid="month-champion">
          <div className="relative w-14 h-14 shrink-0 rounded-full grid place-items-center text-3xl bg-gradient-to-br from-amber-400/20 to-fuchsia-500/25 border border-amber-400/40">
            {champ.avatar_emoji}
            <Crown className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 text-amber-400 fill-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wide text-amber-500">Champion of the Month</div>
            <button onClick={() => navigate(`/app/creators/${champ.owner_id}`)} className="font-display text-xl font-extrabold hover:underline" data-testid="month-champion-name">{champ.owner_name}</button>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">🏆 {champ.wins} weekly win{champ.wins === 1 ? "" : "s"} this month</div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState emoji="🚀" title="The race is warming up" text="No momentum yet this week — be the first to get plays and top the board!" />
      ) : (
        <div className="space-y-2" data-testid="race-list">
          {rows.map((c) => {
            const top3 = c.rank <= 3;
            return (
              <button key={c.owner_id} onClick={() => navigate(`/app/creators/${c.owner_id}`)} data-testid={`race-row-${c.owner_id}`}
                className={`w-full flex items-center gap-4 rounded-2xl border p-3 sm:p-4 text-left card-hover ${top3 ? "border-amber-400/40 bg-amber-400/[0.06]" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`}>
                <div className="w-10 text-center shrink-0">
                  <span className="text-2xl font-display font-extrabold">{MEDAL[c.rank] || c.rank}</span>
                </div>
                <div className="relative w-12 h-12 shrink-0 rounded-full grid place-items-center text-2xl bg-gradient-to-br from-amber-400/15 to-fuchsia-500/20 border border-amber-400/25">
                  {c.avatar_emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate flex items-center gap-1.5">
                    {c.owner_name}
                    {c.rank === 1 && <Trophy className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                  </div>
                  {c.tier_label && <div className="text-[11px] font-semibold truncate" style={{ color: c.tier_color }}>{c.tier_emoji} {c.tier_label}</div>}
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                    <span className="flex items-center gap-1"><Play className="w-3 h-3" /> {c.plays_7d}</span>
                    {c.followers_7d > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> +{c.followers_7d}</span>}
                    {c.tips_7d > 0 && <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> ${c.tips_7d.toFixed(0)}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-lg font-extrabold flex items-center gap-1 justify-end"><Flame className="w-4 h-4 text-orange-400" /> {c.score}</div>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wide">momentum</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {topFans && topFans.length > 0 && (
        <div className="mt-10" data-testid="top-fans-board">
          <div className="flex items-center gap-2 mb-1">
            <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
            <h2 className="font-display text-2xl font-extrabold">Biggest fans this week</h2>
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">The supporters powering creators right now — will you claim the crown? 👑</p>
          {reigning && reigning.streak >= 2 && (
            <div className="mb-4 flex items-center gap-2.5 rounded-2xl border px-4 py-2.5" data-testid="reigning-champion"
              style={{ borderColor: `${(reigning.tier?.color) || "#f59e0b"}66`, background: `linear-gradient(90deg, ${(reigning.tier?.color) || "#f59e0b"}22, transparent)` }}>
              <span className="text-2xl">{reigning.avatar_emoji}</span>
              <div className="leading-tight">
                <div className="text-[11px] font-bold uppercase tracking-wide flex items-center gap-1" style={{ color: (reigning.tier?.color) || "#f59e0b" }}>
                  {reigning.tier?.emoji || "👑"} {reigning.tier?.name || "Reigning"} Champion
                </div>
                <div className="text-sm font-bold">{reigning.name} <span className="font-normal text-[hsl(var(--muted-foreground))]">— {reigning.streak} weeks on top!</span></div>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {topFans.map((f) => {
              const top3 = f.rank <= 3;
              return (
                <button key={f.id} onClick={() => navigate(`/app/creators/${f.id}`)} data-testid={`top-fan-${f.rank}`}
                  className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left card-hover ${f.rank === 1 ? "border-amber-400/50 bg-gradient-to-r from-amber-400/10 to-pink-500/10" : top3 ? "border-pink-500/30 bg-pink-500/[0.05]" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`}>
                  <span className="w-8 text-center text-lg font-extrabold shrink-0">{MEDAL[f.rank] || f.rank}</span>
                  <span className="relative w-11 h-11 shrink-0 rounded-full grid place-items-center text-2xl bg-gradient-to-br from-pink-500/15 to-fuchsia-500/20 border border-pink-500/25">
                    {f.avatar_emoji}
                    {f.rank === 1 && <span className="absolute -top-2 -right-1 text-base">👑</span>}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate flex items-center gap-1.5">{f.name}
                      {f.is_reigning && (
                        <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full shrink-0" data-testid={`reigning-flair-${f.rank}`}
                          style={{ background: `${(f.tier?.color) || "#f59e0b"}33`, color: (f.tier?.color) || "#f59e0b" }}>
                          {f.tier?.emoji || "👑"} {f.streak}x {(f.tier?.name || "CHAMP").toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                      {f.count} tip{f.count === 1 ? "" : "s"}{f.favorite_creator ? <> · biggest supporter of <span className="font-semibold">{f.favorite_creator.avatar_emoji} {f.favorite_creator.name}</span></> : null}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-extrabold text-pink-500 tabular-nums">${f.total.toFixed(0)}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">this week</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {cotw && (
        <div className="mt-10" data-testid="cheer-of-the-week">
          <div className="flex items-center gap-2 mb-3">
            <Quote className="w-5 h-5 text-amber-500" />
            <h2 className="font-display text-2xl font-extrabold">Cheer of the Week</h2>
          </div>
          <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-400/10 to-pink-500/[0.06] p-5">
            <p className="font-display text-lg sm:text-xl leading-snug">“{cotw.message}”</p>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
              <span className="text-xl">{cotw.fan_avatar}</span>
              <span className="font-bold">{cotw.fan_name}</span>
              <span className="text-[hsl(var(--muted-foreground))]">cheered</span>
              <button onClick={() => navigate(`/app/creators/${cotw.creator.id}`)} data-testid="cotw-creator"
                className="font-bold text-[hsl(var(--primary))] hover:underline">{cotw.creator.avatar_emoji} {cotw.creator.name}</button>
              {cotw.creator_hearted && <span className="text-xs px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-500 font-semibold">❤️ Creator loved it</span>}
            </div>
          </div>
        </div>
      )}

      {fame && fame.length > 0 && (
        <div className="mt-10" data-testid="hall-of-fame">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-5 h-5 text-amber-400 fill-amber-400" />
            <h2 className="font-display text-2xl font-extrabold">Hall of Fame</h2>
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Every Creator of the Week, remembered forever 🏆</p>

          {allTime.length > 0 && (
            <div className="mb-6" data-testid="all-time-champions">
              <h3 className="text-xs font-bold uppercase tracking-wide text-amber-500 mb-2">All-time champions</h3>
              <div className="flex flex-wrap gap-2">
                {allTime.map((c) => (
                  <button key={c.owner_id} onClick={() => navigate(`/app/creators/${c.owner_id}`)} data-testid={`champion-${c.owner_id}`}
                    className="flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/[0.06] pl-1.5 pr-3 py-1 card-hover">
                    <span className="w-8 h-8 rounded-full grid place-items-center text-lg bg-gradient-to-br from-amber-400/20 to-fuchsia-500/25 border border-amber-400/25">{c.avatar_emoji}</span>
                    <span className="text-sm font-bold">{c.owner_name}</span>
                    <span className="text-[11px] font-bold text-amber-500">🏆 ×{c.wins}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <h3 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Weekly champions</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {fame.map((w) => (
              <button key={w.week} onClick={() => navigate(`/app/creators/${w.owner_id}`)} data-testid={`fame-${w.week}`}
                className="flex items-center gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-3 text-left card-hover">
                <div className="w-11 h-11 shrink-0 rounded-full grid place-items-center text-2xl bg-gradient-to-br from-amber-400/15 to-fuchsia-500/20 border border-amber-400/25">{w.avatar_emoji}</div>
                <div className="min-w-0">
                  <div className="font-bold truncate">{w.owner_name}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{w.week} · {w.plays_7d} plays · 🔥 {w.score}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="race-share-dialog">
          <DialogHeader>
            <DialogTitle>Share your weekly rank</DialogTitle>
            <DialogDescription>Post this card on social to show off your momentum and pull in new players.</DialogDescription>
          </DialogHeader>
          <div ref={cardRef} data-testid="race-share-card"
            className="rounded-2xl p-6 text-white" style={{ background: themeBg }}>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-90">
              <Trophy className="w-4 h-4" /> GameView Studio · Weekly Race
            </div>
            {mine && mine.in_race ? (
              <div className="mt-4">
                <div className="text-6xl font-display font-black leading-none">#{mine.rank}</div>
                <div className="mt-1 text-lg font-bold">of {mine.total} creators this week</div>
                <div className="mt-3 flex items-center gap-4 text-sm opacity-90">
                  <span>🔥 {mine.score} momentum</span>
                  <span>▶ {mine.plays_7d} plays</span>
                </div>
                <div className="mt-3 text-sm font-semibold text-amber-200">
                  {mine.ahead ? `🎯 ${mine.ahead.plays_to_climb} plays to climb to #${mine.ahead.rank}` : "🏆 I'm Creator of the Week!"}
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <div className="text-3xl font-display font-black">🏆 Creator of the Week</div>
                <div className="mt-1 text-lg font-bold">{(data?.creators?.[0]?.owner_name) || "Race in progress"}</div>
                <div className="mt-3 text-sm opacity-90">Publish & share games to climb the weekly momentum race!</div>
              </div>
            )}
            <div className="mt-5 text-xs opacity-70">Build & play AI games at gameview.studio</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="share-theme-picker">
            <span className="text-xs text-[hsl(var(--muted-foreground))] mr-1">Theme:</span>
            {SHARE_THEMES.map((t) => (
              <button key={t.id} onClick={() => setTheme(t.id)} data-testid={`share-theme-${t.id}`}
                title={t.label} aria-label={t.label}
                className={`w-7 h-7 rounded-full border-2 transition-transform ${theme === t.id ? "border-white scale-110" : "border-transparent"}`}
                style={{ background: t.bg }} />
            ))}
          </div>
          <Button onClick={shareCard} disabled={sharing} className="rounded-full w-full bg-gradient-to-r from-amber-400 to-orange-500 text-black" data-testid="race-share-download">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Share / Download image</>}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
